import { channelPathSegment, registerArchiveUndo } from 'app/lib/channel-archive-undo';
import type { InfiniteData, UseMutationOptions, MutationFunction } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useInfiniteQueryLPC } from 'app/lib/query';
import { http } from 'app/lib/axios';
import type { FunctionalUseInfiniteQueryOptions, FunctionalUseQueryOptions, TokenPagination } from 'app/types/network';
import { useOnMessageReceived } from './index';
import { channelKeys, messageKeys, loanKeys } from '../keys';
import {
  type ChatConversationMemberRole,
  CustomMessageType,
  getOneChannelDefaultInclude,
  MessageKind,
  Channel,
  MemberChannel,
  ChannelListItem,
} from 'app/types/chat';
import type { User } from 'app/types/user';
import { isGroupHeader } from 'app/types/chat';
import type { Listing, ListingMatchStatus } from 'app/types/listing';
import { useQueryKeys } from '../invalidation';
import { trackOnFacebook, CustomFacebookEvent, ContributionType } from 'app/lib/analytics';
import type { ImagePickerAsset } from 'expo-image-picker';
import { asFormData } from 'app/lib/form';
import { recordError } from 'app/lib/monitoring';

/*
  Patch one already-cached channel list query in place when a mutation changes fields that only affect a row,
  such as `silenced` or `unread_message_count`.

  Why this exists:
  invalidating/refetching works well when a channel moves between lists (archive/unarchive), but it is less
  reliable for in-row UI changes because the conversation stays in the same list and we want the row badge/icon
  to update immediately.

  What it does:
  it targets exactly one list cache entry (`channelKeys.list(filters)`) and immutably replaces the matching
  channel inside that infinite-query payload.

  What it does not do:
  it does not scan all channel list queries, and it does not update the channel detail cache. Callers still
  handle detail-cache updates and any follow-up invalidation they need.
*/
function updateChannelInListCache(
  client: ReturnType<typeof useQueryClient>,
  filters: { search?: string; archived?: boolean; listingId?: string; userId?: string },
  channelUrl: Channel['channel_url'],
  updater: (channel: Channel) => Channel,
) {
  client.setQueryData<InfiniteData<TokenPagination<ChannelListItem>>>(channelKeys.list(filters), (current) => {
    if (!current?.pages) return current;

    const pages = current.pages.map((page) => {
      const results = page.results.map((item) => {
        if (isGroupHeader(item) || item.channel_url !== channelUrl) return item;
        return updater(item);
      });

      const pageChanged = results.some((item, index) => item !== page.results[index]);

      return pageChanged ? { ...page, results } : page;
    });

    const hasChanged = pages.some((page, index) => page !== current.pages[index]);

    return hasChanged ? { ...current, pages } : current;
  });
}

export function useAllChannels(
  {
    search,
    archived,
    listingId,
    userId,
  }: { search?: string; archived?: boolean; listingId?: Listing['id']; userId?: string } = {},
  opts: Omit<FunctionalUseInfiniteQueryOptions<TokenPagination<ChannelListItem>>, 'initialPageParam'> = {},
) {
  const client = useQueryClient();
  return useInfiniteQueryLPC<TokenPagination<ChannelListItem>>({
    queryKey: channelKeys.list({ search, archived, listingId, userId }),
    queryFn: async ({ pageParam }) => {
      const res = await http.get<TokenPagination<ChannelListItem>>('/chat/channels', {
        params: { search, archived, listingId, userId, token: pageParam },
      });

      // onSuccess
      res.data.results.forEach((item) => {
        if (!isGroupHeader(item)) {
          client.setQueryData(channelKeys.detail(item.channel_url), item);
        }
      });
      if (!archived) {
        void client.invalidateQueries({ queryKey: messageKeys.unread() });
      }

      return res.data;
    },
    ...opts,
    initialPageParam: 0,
    getNextPageParam: (previousPage) => previousPage.next ?? undefined,
  });
}

export enum ChannelType {
  Listing = 'listing',
  Member = 'member',
}
export function useCreateChannel<T>(channelType: ChannelType, opts: UseMutationOptions<Channel, Error, T> = {}) {
  const queryListing = useCreateListingChannel(opts as UseMutationOptions<Channel, Error, Pick<Listing, 'id'>>);
  const queryMember = useCreateMemberChannel(opts as UseMutationOptions<MemberChannel, Error, Pick<User, 'id'>>);
  return channelType === ChannelType.Listing ? queryListing : queryMember;
}

export function useCreateListingChannel(opts: UseMutationOptions<Channel, Error, Pick<Listing, 'id'>> = {}) {
  const client = useQueryClient();

  return useMutation<Channel, Error, Pick<Listing, 'id'>>({
    mutationFn: async (listing) =>
      http.post<Channel>('/chat/channels', { listingId: listing.id }).then((res) => res.data),
    ...opts,
    onSuccess(result, variables, context) {
      client.setQueryData(channelKeys.detail(result.channel_url), result);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useCreateMemberChannel(opts: UseMutationOptions<MemberChannel, Error, Pick<User, 'id'>> = {}) {
  const client = useQueryClient();

  return useMutation<MemberChannel, Error, Pick<User, 'id'>>({
    mutationFn: async (user) => http.post<MemberChannel>('/chat/channels', { userId: user.id }).then((res) => res.data),
    ...opts,
    onSuccess(result, variables, context) {
      client.setQueryData(channelKeys.detail(result.channel_url), result);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useMarkNeedMatchAs(
  opts: UseMutationOptions<
    boolean,
    Error,
    { channelUrl: Channel['channel_url']; listingId: Listing['id']; status: ListingMatchStatus }
  > = {},
) {
  const qk = useQueryKeys();
  return useMutation({
    mutationFn: async ({ listingId, status }) =>
      http.post<boolean>(`/listings/${listingId}/mark-need-match`, { status }).then((res) => res.data),
    ...opts,
    async onSuccess(data, variables, context) {
      await Promise.all([
        qk.channel.invalidate(variables.channelUrl),
        qk.activeChannels.invalidate(),
        qk.archivedChannels.invalidate(),
      ]);
      return opts.onSuccess?.(data, variables, context);
    },
  });
}

export function useLinkOfferToChannel(
  opts: UseMutationOptions<Channel, Error, { channelUrl: Channel['channel_url']; offerId: Listing['id'] | null }> = {},
) {
  const client = useQueryClient();
  const qk = useQueryKeys();
  return useMutation({
    mutationFn: async ({ channelUrl, offerId }) =>
      http.put<Channel>(`/chat/channels/${channelUrl}/link-offer`, { offerId }).then((res) => res.data),
    ...opts,
    async onSuccess(result, variables, context) {
      client.setQueryData(channelKeys.detail(result.channel_url), result);
      await Promise.all([
        qk.channel.invalidate(variables.channelUrl),
        qk.activeChannels.invalidate(),
        qk.archivedChannels.invalidate(),
      ]);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useMarkChannelAsRead(opts: UseMutationOptions<void, Error, Channel['channel_url']> = {}) {
  const client = useQueryClient();
  const qk = useQueryKeys();
  return useMutation({
    mutationFn: async (url) => await http.put(`/chat/channels/${url}/messages/mark-as-read`),
    ...opts,
    async onSuccess(data, url, context) {
      // Reading a channel changes its unread state, not its messages. Update every cached detail
      // variant in place so this mutation does not refetch the expensive channel + messages payload.
      client.setQueriesData<Channel>({ queryKey: channelKeys.detailsForOne(url) }, (channel) =>
        channel ? { ...channel, unread_message_count: 0 } : channel,
      );
      await Promise.all([qk.activeChannels.invalidate(), qk.unreadMessages.invalidate()]);
      return opts.onSuccess?.(data, url, context);
    },
  });
}

export function useDeleteMessage(
  opts: UseMutationOptions<void, Error, { channelUrl: Channel['channel_url']; messageId: string }> = {},
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ channelUrl, messageId }) =>
      await http.delete(`/chat/channels/${channelUrl}/messages/${messageId}`),
    ...opts,
    async onSuccess(data, variables, context) {
      await client.invalidateQueries({ queryKey: channelKeys.detail(variables.channelUrl) });
      void client.invalidateQueries({ queryKey: channelKeys.list({ archived: false }) });
      return opts.onSuccess?.(data, variables, context);
    },
  });
}

export function useEditMessage(
  opts: UseMutationOptions<
    void,
    Error,
    { channelUrl: Channel['channel_url']; messageId: string; message: string }
  > = {},
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ channelUrl, messageId, message }) =>
      await http.put(`/chat/channels/${channelUrl}/messages/${messageId}`, { message }),
    ...opts,
    onSuccess(data, variables, context) {
      void client.invalidateQueries({ queryKey: channelKeys.detail(variables.channelUrl) });
      void client.invalidateQueries({ queryKey: channelKeys.list({ archived: false }) });
      return opts.onSuccess?.(data, variables, context);
    },
  });
}

export function useBatchUnreadChannels({
  search,
  archived,
  listingId,
  userId,
  ...opts
}: UseMutationOptions<void, Error, Channel['channel_url'][]> & {
  search?: string;
  archived?: boolean;
  listingId?: string;
  userId?: string;
} = {}) {
  const mutate: MutationFunction<void, Channel['channel_url'][]> = (urls) =>
    http.put(`chat/channels/batch-unread`, urls).then((res) => res.data);
  const client = useQueryClient();
  return useMutation({
    mutationFn: mutate,
    ...opts,
    async onSuccess(_, variables, context) {
      variables.forEach((url) => {
        updateChannelInListCache(client, { search, archived, listingId, userId }, url, (channel) => ({
          ...channel,
          // Keep at least one unread so the channel is marked unread without inflating higher counts.
          unread_message_count: Math.max(channel.unread_message_count, 1),
        }));
        client.setQueriesData<Channel>({ queryKey: channelKeys.detailsForOne(url) }, (current) =>
          current
            ? {
                ...current,
                // Keep at least one unread so the detail cache matches the list badge state.
                unread_message_count: Math.max(current.unread_message_count, 1),
              }
            : current,
        );
      });
      await client.invalidateQueries({ queryKey: messageKeys.unread() });
      return opts.onSuccess?.(_, variables, context);
    },
  });
}
export const NO_CHANNEL_SELECTED = 'no-channel-selected';

export function useOneChannel(
  url?: string,
  include: string[] = getOneChannelDefaultInclude,
  opts: FunctionalUseQueryOptions<Channel | undefined | null> = {},
) {
  const client = useQueryClient();
  const query = useQuery<Channel | undefined | null>({
    queryKey: channelKeys.detail(url ?? 'meh', include),
    queryFn: async () => {
      if (!url || url === NO_CHANNEL_SELECTED) return null;
      const res = await http.get<Channel>(`/chat/channels/${url}?include=${include.join(',')}`);
      if (res.data.messages) {
        res.data.messages.forEach((m) => {
          if (m.type === MessageKind.Message && m.custom_type === CustomMessageType.Loan) {
            client.setQueryData(loanKeys.detail(m.loan.id), m.loan);
          }
        });
      }

      return res.data;
    },
    enabled: !!url && url !== NO_CHANNEL_SELECTED,
    ...opts,
  });

  return query;
}

export type RefetchOneChannel = ReturnType<typeof useOneChannel>['refetch'];

export function useBatchArchiveChannels(opts: UseMutationOptions<void, Error, Channel['channel_url'][]> = {}) {
  const onMessagedReceivedCallback = useOnMessageReceived();
  const mutate: MutationFunction<void, Channel['channel_url'][]> = (urls) =>
    http.put(`chat/channels/batch-archive`, urls).then((res) => res.data);
  const client = useQueryClient();
  return useMutation({
    mutationFn: mutate,
    ...opts,
    async onSuccess(_, variables, context) {
      await Promise.all(variables.map((url) => onMessagedReceivedCallback(url)));
      await client.invalidateQueries({ queryKey: channelKeys.lists() });
      return opts.onSuccess?.(_, variables, context);
    },
  });
}

export function useArchiveChannel({
  invalidateRecents = false,
  invalidateRecentsDelayMs = 0,
  listingId,
  userId,
  ...opts
}: UseMutationOptions<void, Error, Channel['channel_url']> & {
  invalidateRecents?: boolean;
  invalidateRecentsDelayMs?: number;
  listingId?: string;
  userId?: string;
} = {}) {
  const qk = useQueryKeys();
  return useMutation({
    mutationFn: async (url) => {
      await http.put(`/chat/channels/${channelPathSegment(url)}/archive`);
      registerArchiveUndo(url);
    },
    ...opts,
    async onSuccess(data, url, context) {
      await qk.archivedChannels.invalidate(listingId, userId);
      if (invalidateRecents) {
        if (invalidateRecentsDelayMs > 0) {
          await new Promise((resolve) => setTimeout(resolve, invalidateRecentsDelayMs));
        }
        await qk.activeChannels.invalidate(listingId, userId);
      }
      return opts.onSuccess?.(data, url, context);
    },
  });
}

export function useUnArchiveChannel({
  invalidateArchived = true,
  listingId,
  userId,
  ...opts
}: UseMutationOptions<void, Error, Channel['channel_url']> & {
  invalidateArchived?: boolean;
  listingId?: string;
  userId?: string;
} = {}) {
  const qk = useQueryKeys();
  return useMutation({
    mutationFn: async (url) => http.delete(`/chat/channels/${channelPathSegment(url)}/archive`),
    ...opts,
    async onSuccess(data, url, context) {
      await qk.activeChannels.invalidate(listingId, userId);
      if (invalidateArchived) {
        await qk.archivedChannels.invalidate(listingId, userId);
      }
      return opts.onSuccess?.(data, url, context);
    },
  });
}

export function useUpdateChannelSilenced({
  search,
  archived,
  listingId,
  userId,
  ...opts
}: UseMutationOptions<Channel, Error, { url: Channel['channel_url']; silenced: boolean; durationMinutes?: number }> & {
  search?: string;
  archived?: boolean;
  listingId?: string;
  userId?: string;
} = {}) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async ({ url, silenced, durationMinutes }) =>
      http.put<Channel>(`/chat/channels/${url}/silence`, { silenced, durationMinutes }).then((res) => res.data),
    ...opts,
    onSuccess(data, variables, context) {
      client.setQueriesData<Channel>({ queryKey: channelKeys.detailsForOne(variables.url) }, (current) =>
        current ? { ...current, silenced: data.silenced, silenced_until: data.silenced_until } : current,
      );
      updateChannelInListCache(client, { search, archived, listingId, userId }, variables.url, (channel) =>
        channel.silenced === data.silenced && channel.silenced_until === data.silenced_until
          ? channel
          : { ...channel, silenced: data.silenced, silenced_until: data.silenced_until },
      );
      return opts.onSuccess?.(data, variables, context);
    },
  });
}

export function useToggleReaction(
  opts: UseMutationOptions<void, Error, { channelUrl: Channel['channel_url']; messageId: string; emoji: string }> = {},
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ channelUrl, messageId, emoji }) =>
      http.put(`/chat/channels/${channelUrl}/messages/${messageId}/reactions`, { emoji }).then((res) => res.data),
    ...opts,
    async onSuccess(data, variables, context) {
      await client.invalidateQueries({ queryKey: channelKeys.detail(variables.channelUrl) });
      trackOnFacebook(CustomFacebookEvent.Contributed, { contribution_type: ContributionType.SentMessage });
      return opts.onSuccess?.(data, variables, context);
    },
  });
}
export const SUPPORT_CHANNEL_URL = 'lpc-support';
export const SUPPORT_TRANSPORT_CHANNEL_URL = 'lpc-support-transport';

export interface CreateGroupConversationInput {
  name: string;
  userIds: string[];
  picture?: ImagePickerAsset;
}

export function useCreateGroupConversation(
  opts: UseMutationOptions<Channel, Error, CreateGroupConversationInput> = {},
) {
  const client = useQueryClient();
  const qk = useQueryKeys();
  return useMutation<Channel, Error, CreateGroupConversationInput>({
    mutationFn: async (payload) => {
      const { picture, ...rest } = payload;
      const channel = await http.post<Channel>('/chat/conversations', rest).then((res) => res.data);
      if (picture) {
        void http
          .put<Channel>(`/chat/conversations/${channel.channel_url}/picture`, asFormData({ picture }), {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
          .catch(recordError);
      }
      return channel;
    },
    ...opts,
    async onSuccess(result, variables, context) {
      client.setQueryData(channelKeys.detail(result.channel_url), result);
      await qk.activeChannels.invalidate();
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useUpdateGroupConversation(
  opts: UseMutationOptions<Channel, Error, { url: Channel['channel_url']; name?: string; description?: string }> = {},
) {
  const client = useQueryClient();
  const qk = useQueryKeys();
  return useMutation({
    mutationFn: async ({ url, ...payload }) =>
      http.put<Channel>(`/chat/conversations/${url}`, payload).then((res) => res.data),
    ...opts,
    async onSuccess(result, variables, context) {
      client.setQueryData(channelKeys.detail(result.channel_url), result);
      await Promise.all([qk.channel.invalidate(variables.url), qk.activeChannels.invalidate()]);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useUpdateGroupConversationPicture(
  opts: UseMutationOptions<Channel, Error, { url: Channel['channel_url']; picture: ImagePickerAsset }> = {},
) {
  const client = useQueryClient();
  const qk = useQueryKeys();
  return useMutation({
    mutationFn: async ({ url, picture }) =>
      http
        .put<Channel>(`/chat/conversations/${url}/picture`, asFormData({ picture }), {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((res) => res.data),
    ...opts,
    async onSuccess(result, variables, context) {
      client.setQueryData(channelKeys.detail(result.channel_url), result);
      await Promise.all([qk.channel.invalidate(variables.url), qk.activeChannels.invalidate()]);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useLeaveGroupConversation(
  opts: UseMutationOptions<void, Error, { url: Channel['channel_url']; userId: User['id'] }> = {},
) {
  const qk = useQueryKeys();
  return useMutation({
    mutationFn: async ({ url, userId }) => {
      await http.delete(`/chat/conversations/${url}/members/${userId}`);
    },
    ...opts,
    async onSuccess(data, variables, context) {
      await Promise.all([qk.channel.invalidate(variables.url), qk.activeChannels.invalidate()]);
      return opts.onSuccess?.(data, variables, context);
    },
  });
}

export function useAddConversationMembers(
  opts: UseMutationOptions<Channel, Error, { url: Channel['channel_url']; userIds: string[] }> = {},
) {
  const client = useQueryClient();
  const qk = useQueryKeys();
  return useMutation({
    mutationFn: async ({ url, userIds }) =>
      http.post<Channel>(`/chat/conversations/${url}/members`, { userIds }).then((res) => res.data),
    ...opts,
    async onSuccess(result, variables, context) {
      client.setQueryData(channelKeys.detail(result.channel_url), result);
      await qk.channel.invalidate(variables.url);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useUpdateConversationMemberRole(
  opts: UseMutationOptions<
    Channel,
    Error,
    { url: Channel['channel_url']; userId: User['id']; role: ChatConversationMemberRole }
  > = {},
) {
  const client = useQueryClient();
  const qk = useQueryKeys();
  return useMutation({
    mutationFn: async ({ url, userId, role }) =>
      http.put<Channel>(`/chat/conversations/${url}/members/${userId}/role`, { role }).then((res) => res.data),
    ...opts,
    async onSuccess(result, variables, context) {
      client.setQueryData(channelKeys.detail(result.channel_url), result);
      await qk.channel.invalidate(variables.url);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}
